import { getIn } from 'final-form';

import { useOrderStore } from '@blocks/stores/order';

import { getDataBox } from '../api';
import { parseSearchParams } from '../utils';

export function createPoliciesDecorator(form) {
  return form.subscribe(
    ({
      submitError,
      hasSubmitErrors,
      dirtyFieldsSinceLastSubmit,
      values: { showGdprByDefault },
    }) => {
      const ERRORS = ['policy_required', 'policy_agreement_not_full'];

      if (hasSubmitErrors && ERRORS.includes(submitError)) {
        form.change('showGdpr', true);

        if (dirtyFieldsSinceLastSubmit.login) {
          form.mutators.clearSubmitError();

          form.batch(() => {
            form.change('showGdpr', showGdprByDefault);
            form.change('policy_agree', false);
            form.change('marketing_agree', false);
          });
        }
      }
    },
    {
      values: true,
      submitError: true,
      hasSubmitErrors: true,
      dirtyFieldsSinceLastSubmit: true,
    },
  );
}

export function createGDPRShowByDefaultDecorator(form) {
  return form.subscribe(
    ({ values: { login_ability } }) => {
      if (login_ability) {
        form.batch(() => {
          form.change('showGdpr', false);
          form.change('policy_agree', false);
          form.change('marketing_agree', false);
        });
      }
    },
    {
      values: true,
    },
  );
}

export function createWriterDecorator(form) {
  let writerCache;

  return form.subscribe(
    ({ values }) => {
      const { writer_req_slug } = values;

      if (writer_req_slug) {
        if (writerCache !== writer_req_slug) {
          writerCache = writer_req_slug;

          form.change('showWriter', true);
        }
      } else {
        writerCache = undefined;

        form.batch(() => {
          form.change('showWriter', false);
          form.change('writer_req_slug', '');
        });
      }
    },
    {
      values: true,
    },
  );
}

export function createAnalyticsDecorator(form) {
  const addGtmEvent = (event = {}) => {
    if (Array.isArray(dataLayer)) {
      dataLayer.push({
        event: 'autoEvent',
        eventCategory: 'OrderFormSmallW',
        ...event,
      });
    }
  };

  return form.subscribe(
    ({ submitFailed, submitSucceeded, submitError, values, errors }) => {
      if (!Array.isArray(window.dataLayer)) return false;

      const { pathname = '/' } = location || {};
      const isNewUser = values.policy_agree ? 'NewUser' : 'ReturnUser';

      if (submitFailed) {
        addGtmEvent({
          eventAction: isNewUser,
          eventLabel: 'ButtonClick_w_errors',
        });

        Object.keys(errors).forEach(error =>
          addGtmEvent({
            eventAction: 'Errors',
            eventLabel: error,
          }),
        );
      }

      if (submitError && submitError !== 'auth_token_not_defined') {
        addGtmEvent({
          eventAction: 'Errors',
          eventLabel: submitError,
        });
      }

      if (submitSucceeded) {
        addGtmEvent({
          eventAction: isNewUser,
          eventLabel: 'ButtonClick_ok',
        });

        if (isNewUser) {
          addGtmEvent({
            eventCategory: 'Login',
            eventAction: 'NewUser',
            eventLabel: pathname,
          });
        } else {
          addGtmEvent({
            eventCategory: 'Login',
            eventAction: 'Return_customer',
            eventLabel: pathname,
          });
        }
      }
    },
    {
      submitFailed: true,
      submitSucceeded: true,
      submitError: true,
      values: true,
      errors: true,
    },
  );
}

export function createDataBoxDecorator(form) {
  return form.subscribe(
    ({ initialValues }) => {
      const { data_box_key } = parseSearchParams(location.search);

      if (data_box_key) {
        getDataBox({ data_box_key, domain: '/' }).then(({ databox }) => {
          // if (
          //   paper_type &&
          //   pages_qty &&
          //   deadline_datetime &&
          //   words_qty &&
          //   databox['paper-size-type']
          // ) {

          if (Object.keys(databox).length) {
            useOrderStore.getState().clearOrderStore();

            form.initialize({
              ...initialValues,
              ...databox,
            });

            const searchParams = new URLSearchParams(window.location.search);
            searchParams.delete('data_box_key');
            const stringifiedParams = searchParams.toString();
            const baseUrl =
              window.location.protocol + '//' + window.location.host + window.location.pathname;
            const path = stringifiedParams ? `${baseUrl}?${stringifiedParams}` : baseUrl;
            window.history.pushState({ path }, '', path);
          }
        });
      }
    },
    {
      initialValues: true,
    },
  );
}

const noop = () => {};
const getFields = name =>
  [...document.querySelector(`[name="${name}"]`)?.elements].filter(
    el => typeof el.focus === 'function',
  );

const findField = (fields, errors) => fields.find(field => field.name && getIn(errors, field.name));
/**
 * @param {String} name - form name
 * @param {Array} errors - errors/submitErrors
 */
const focusOnFirstError = (name, errors) => {
  const firstInput = findField(getFields(name), errors);

  if (firstInput) {
    firstInput.focus();
  }
};

export function createFocusDecorator(form) {
  return form.subscribe(
    ({ initialValues, errors, submitErrors }) => {
      const originalSubmit = form.submit;
      const { name } = initialValues;

      const afterSubmit = () => {
        if (errors && Object.keys(errors).length) {
          focusOnFirstError(name, errors);
        } else if (submitErrors && Object.keys(submitErrors).length) {
          focusOnFirstError(name, submitErrors);
        }
      };

      form.submit = () => {
        const result = originalSubmit.call(form);

        if (result && typeof result.then === 'function') {
          result.then(afterSubmit, noop);
        } else {
          afterSubmit();
        }

        return result;
      };

      return () => {
        form.submit = originalSubmit;
      };
    },
    {
      errors: true,
      submitErrors: true,
      initialValues: true,
    },
  );
}

export function createTitleDecorator(form) {
  return form.subscribe(
    ({ values }) => {
      const { title } = values;

      if (title) {
        return form.change('showTitle', true);
      }

      form.batch(() => {
        form.change('showTitle', false);
        form.change('title', undefined);
      });
    },
    {
      values: true,
    },
  );
}
